import { AxiosPromise } from 'axios';
import request from '../utils/request';
import {
  AppForSa,
  DelegatorAccountResDto,
  DeprovisionDetail,
  DeprovisioningAppResDto,
  ReAuthAvailableAppResDto,
  AuthenticateTokenList,
  ApiTokenDto,
  UpdateApiToken,
  CreateAdmin,
  CreateRoleReqDto,
  UpdateAdmin,
  AdministratorProps,
  RoleProps,
  EuqSettingsList,
  EuqEmailReportResponse,
  EuqEmailReportUpdateRequest,
} from '../types/administrator';
import { TokenStatus } from '@Types/provision';

function apiGetDelegatorAccount(): AxiosPromise<DelegatorAccountResDto[]> {
  return request({
    url: `/servicesAccount/delegatorAccounts`,
    method: 'get',
    headers: { 'x-org-id': 'all' },
  });
}

function apiGetTokenStatus(orgId: string): AxiosPromise<
  {
    app: AppForSa;
    appId: string;
    tokenStatus: TokenStatus;
    appOwner: number;
    tokenTypes: number[];
    [key: string]: any;
  }[]
> {
  return request({
    url: `/apps/tokens`,
    headers: { 'x-org-id': orgId },
    method: 'get',
    data: {},
  });
}

function apiGetApiAuthenticateToken(): AxiosPromise<AuthenticateTokenList[]> {
  return request({
    url: `/apiTokens`,
    method: 'get',
  });
}
function apiPostApiAuthenticateToken(data: ApiTokenDto) {
  return request({
    method: 'post',
    url: `/apiTokens`,
    data,
  });
}

function apiUpdateAuthenticateToken(data: UpdateApiToken) {
  return request({
    method: 'put',
    url: `/apiTokens`,
    data,
  });
}
function apiDeleteApiAuthenticateToken(accessToken: string) {
  return request({
    url: `/apiTokens/${accessToken}`,
    method: 'delete',
  });
}

function apiRefreshApiAuthenticateToken(accessToken: { accessToken: string }) {
  return request({
    url: `/apiTokens/refresh`,
    method: 'post',
    data: accessToken,
  });
}
function apiTokensO365TokenExistence() {
  return request({
    url: `/apiTokens/O365Token/existence`,
    method: 'get',
  });
}
function apiExtendExchangeServerExpiration(orgId: string) {
  return request({
    url: `apps/exchangeServer/tokens/extend`,
    headers: { 'x-org-id': orgId },
    method: 'post',
  });
}

/**
 * @description deprovision Gmail or Exchange, need to check XDR status
 */
function apiCheckAddOnStatus(
  app: AppForSa.Gmail | AppForSa.Exchange,
  orgId: string
) {
  return request({
    url: `apps/${app}/addOnStatus`,
    headers: { 'x-org-id': orgId },
    method: 'get',
  });
}

/**
 * @description get all reAuth available app
 * @returns return all org with them apps which can do reAuth
 * @example
 * {
 *    "4f984626-4d31-4757-89c9-e45fb84156c4": ["exchange"]
 * }
 */
function apiGetAllReAuthAvailableApp(): AxiosPromise<ReAuthAvailableAppResDto> {
  return request({
    url: `apps/reAuth/available`,
    method: 'get',
    headers: {
      'x-org-id': 'all',
    },
  });
}

/**
 * @description get all deprovisioning app
 * @example
 * {
 *   "4f984626-4d31-4757-89c9-e45fb84156c4": {
 *     "exchange": {
 *       "status": "deProvisioning" | "failure"
 *     }
 *   }
 * }
 */
function apiGetAllDeprovisioningApp(): AxiosPromise<DeprovisioningAppResDto> {
  return request({
    url: `apps/deProvision/status/unfinished`,
    method: 'get',
    headers: {
      'x-org-id': 'all',
    },
  });
}

/**
 * @description get app deprovision status, deprovisioning or deprovision failed
 */
function apiGetAppDeprovisionStatus(
  orgId: string,
  app: AppForSa
): AxiosPromise<{ status: string; tasks: DeprovisionDetail[] }> {
  return request({
    url: `apps/${app}/deProvision/tasks/status`,
    method: 'get',
    headers: {
      'x-org-id': orgId,
    },
  });
}

function apiChangDeprecatedSpodPassword(
  orgId: string,
  email: string,
  password: string
) {
  return request({
    method: 'put',
    url: `apps/deprecatedSpod/accounts/${email}`,
    headers: { 'x-org-id': orgId },
    data: {
      adminPwd: password,
    },
  });
}

function apiGetXDRAddOnStatus(
  orgId: string,
  app: AppForSa.Gmail | AppForSa.Exchange
): AxiosPromise<boolean> {
  return request({
    method: 'get',
    url: `apps/${app}/deProvision/xdrAddOnStatus`,
    headers: { 'x-org-id': orgId },
  });
}

function getLocalAccounts(): AxiosPromise<AdministratorProps[]> {
  return request({
    url: `/localAccounts`,
    method: 'get',
  });
}

function createLocalAccounts(createLocalAccountsReqDto: CreateAdmin) {
  return request({
    url: `/localAccounts`,
    method: 'post',
    data: createLocalAccountsReqDto,
  });
}

function updateLocalAccountsByEmail(email: string, data: UpdateAdmin) {
  return request({
    url: `/localAccounts/${email}`,
    method: 'put',
    data,
  });
}

function getLocalAccountsByEmail(email: string): AxiosPromise<CreateAdmin> {
  return request({
    url: `/localAccounts/${email}`,
    method: 'get',
  });
}

function deleteLocalAccountsByEmail(email: string) {
  return request({
    url: `/localAccounts/${email}`,
    method: 'delete',
  });
}

function resetPasswordByEmail(email: string) {
  return request({
    url: `/localAccounts/${email}/forgotPassword`,
    method: 'post',
  });
}

function getRoles(): AxiosPromise<RoleProps[]> {
  return request({
    url: `/roles`,
    method: 'get',
  });
}

function getRoleByRoleId(roleId: string) {
  return request({
    url: `/roles/${roleId}`,
    method: 'get',
  });
}

function deleteRoleByRoleId(roleId: string) {
  return request({
    url: `/roles/${roleId}`,
    method: 'delete',
  });
}

function updateRoles(roleId: string, data: CreateRoleReqDto) {
  return request({
    url: `/roles/${roleId}`,
    method: 'put',
    data,
  });
}

function createRoles(data: CreateRoleReqDto) {
  return request({
    url: `/roles`,
    method: 'post',
    data,
  });
}

function isMultiTenantUser(email: string) {
  return request({
    url: `/localAccounts/${email}/multiTenants/availability`,
    method: 'get',
  });
}

function getEuqSettings(
  orgId?: string,
  platform?: string
): AxiosPromise<EuqSettingsList> {
  const req = request({
    url: `euqSettings`,
    method: 'get',
    ...(orgId && { headers: { 'x-org-id': orgId } }),
    ...(platform && { params: { platform } }),
  });
  return req;
}

function updateEuqSettings(
  euqSettingsList: EuqSettingsList,
  platform?: string
) {
  return request({
    method: 'post',
    url: `euqSettings`,
    data: euqSettingsList,
    ...(platform && { params: { platform } }),
  });
}

function getEuqEmailReport(
  orgId?: string
): AxiosPromise<EuqEmailReportResponse> {
  return request({
    url: `euqEmailReport`,
    method: 'get',
    ...(orgId && { headers: { 'x-org-id': orgId } }),
  });
}

function updateEuqEmailReport(data: EuqEmailReportUpdateRequest) {
  return request({
    method: 'post',
    url: `euqEmailReport`,
    data,
  });
}

export default {
  apiGetDelegatorAccount,
  apiGetTokenStatus,
  apiGetApiAuthenticateToken,
  apiPostApiAuthenticateToken,
  apiUpdateAuthenticateToken,
  apiDeleteApiAuthenticateToken,
  apiRefreshApiAuthenticateToken,
  apiTokensO365TokenExistence,
  apiExtendExchangeServerExpiration,
  apiCheckAddOnStatus,
  apiGetAllReAuthAvailableApp,
  apiGetAllDeprovisioningApp,
  apiGetAppDeprovisionStatus,
  apiChangDeprecatedSpodPassword,
  apiGetXDRAddOnStatus,
  getLocalAccounts,
  createLocalAccounts,
  updateLocalAccountsByEmail,
  getLocalAccountsByEmail,
  deleteLocalAccountsByEmail,
  resetPasswordByEmail,
  getRoles,
  getRoleByRoleId,
  deleteRoleByRoleId,
  updateRoles,
  createRoles,
  isMultiTenantUser,
  getEuqSettings,
  updateEuqSettings,
  getEuqEmailReport,
  updateEuqEmailReport,
};
